import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ACTIONS_KEY,
  ADDRESS_KEY,
  AVARAGE_CONSUMPTION_KEY,
  AVARAGE_SPEED_KEY,
  CO2_EMISSION_KEY,
  CUSTOMER_KEY,
  DECLINE_KEY,
  DISTANCE_KEY,
  DRIVER_KEY,
  DURATION_KEY,
  ECO_DRIVE_KEY,
  FUEL_CONSUMPTION_CAN_KEY,
  INCREASE_KEY,
  INFO_FOR_PARKING_KEY,
  INFO_KEY,
  LOCKED_KEY,
  MAXIMAL_SPEED_KEY,
  MILEAGE_END_KEY,
  OVERVIEW_KEY,
  PRIVATE_KEY,
  SOURCE_KEY,
  TIMESTAMP_KEY,
  VEHICLE_KEY,
} from '../constants';
import TooltipComponent from '../../TooltipComponent';
import ThAvarageSpeed from '../TableHeadCell/ThAvarageSpeed';
import ThAverageConsumption from '../TableHeadCell/ThAverageConsumption';
import ThConsumption from '../TableHeadCell/ThConsumption';
import ThCustomer from '../TableHeadCell/ThCustomer';
import ThDateTime from '../TableHeadCell/ThDateTime';
import ThDecline from '../TableHeadCell/ThDecline';
import ThDistance from '../TableHeadCell/ThDistance';
import ThDriver from '../TableHeadCell/ThDriver';
import ThDuration from '../TableHeadCell/ThDuration';
import ThEcoDrive from '../TableHeadCell/ThEcoDrive';
import ThSource from '../TableHeadCell/ThSource';
import ThIncrease from '../TableHeadCell/ThIncrease';
import ThLockded from '../TableHeadCell/ThLocked';
import ThMaximalSpeed from '../TableHeadCell/ThMaximalSpeed';
import ThMileageEnd from '../TableHeadCell/ThMileageEnd';
import ThMode from '../TableHeadCell/ThMode';
import { ThInfoForRide } from '../TableHeadCell/ThInfoForRide';
import { ICheckedTableItems, ITripsOverviewColumn, ITripsOverviewTableData } from '../interfaces';
import {
  checkedTableItemsAtom,
  columnsAtom,
  headerWidthAtom,
  rideBookDataAtom,
  rideItemAtom,
  rideItemIdsAtom,
} from '../TripsOverviewState';
import ThCo2Emission from '../TableHeadCell/ThCo2Emission';

export const getIsColumnLast = (columnsTable: ITripsOverviewColumn[], key: string): boolean => {
  const columns = columnsTable.filter((i) => !i.hidden && i.key !== ACTIONS_KEY);
  return columns[columns.length - 1]?.key !== key;
};
interface ITableHeaderProps {
  selectAllRows: (event: React.ChangeEvent<HTMLInputElement>) => void;
  columns: ITripsOverviewColumn[];
}

function TableHeader(props: ITableHeaderProps): JSX.Element {
  const checkedTableItems = useRecoilValue<ICheckedTableItems>(checkedTableItemsAtom);
  const columnsTable = useRecoilValue<ITripsOverviewColumn[]>(columnsAtom);
  const rideItemIds = useRecoilValue<string[]>(rideItemIdsAtom);
  const tableData = useRecoilValue<ITripsOverviewTableData | null>(rideBookDataAtom);
  const rideItem = useRecoilValue(rideItemAtom(rideItemIds[0]));
  const { formatMessage: f } = useIntl();

  const setHeaderWidth = useSetRecoilState<string>(headerWidthAtom);

  const refSize: React.MutableRefObject<any> = React.useRef<HTMLTableRowElement>();
  React.useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      setHeaderWidth(`${entries[0].contentRect.width - 100}px`);
    });
    observer.observe(refSize.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => refSize.current && observer.unobserve(refSize.current);
  }, [setHeaderWidth]);

  const renderHeaderCell = (column: ITripsOverviewColumn): JSX.Element | null => {
    switch (column.key) {
      case LOCKED_KEY:
        return <ThLockded id={LOCKED_KEY} />;

      case TIMESTAMP_KEY:
        return <ThDateTime id={TIMESTAMP_KEY} />;

      case VEHICLE_KEY:
        return (
          <div
            id="table-header-col-vehicle"
            className="th-sticky"
            style={{ whiteSpace: 'nowrap', flex: 1, minWidth: '200px' }}
          >
            <TooltipComponent
              id={`ridebook-${VEHICLE_KEY}`}
              overlay={
                <div style={{ color: '#fff', whiteSpace: 'pre-wrap', fontWeight: 'normal' }}>
                  {`${f({
                    id: 'tripsOverview.modal.vehicle',
                  })}: `}
                  <strong>
                    {`${f({
                      id: 'tables.tripsOverview.header.tooltip.vehicle',
                    })}`}
                  </strong>
                </div>
              }
              width="300px"
              style={{ top: '35px' }}
              positionLeft
            >
              {f({ id: 'tripsOverview.modal.vehicle' })}
            </TooltipComponent>
          </div>
        );

      case PRIVATE_KEY:
        return <ThMode id={PRIVATE_KEY} />;

      case ADDRESS_KEY:
        return (
          <div className="th-sticky" style={{ whiteSpace: 'nowrap', minWidth: '350px', flex: '1' }}>
            <TooltipComponent
              id={`ridebook-${ADDRESS_KEY}`}
              overlay={
                <div style={{ color: '#fff', whiteSpace: 'pre-wrap', fontWeight: 'normal' }}>
                  {`${f({
                    id: 'tables.tripsOverview.column.address',
                  })}: `}
                  <strong>
                    {`${f({
                      id: 'tables.tripsOverview.header.tooltip.address',
                    })}`}
                  </strong>
                </div>
              }
              width="300px"
              style={{ top: '35px' }}
              positionLeft={getIsColumnLast(columnsTable, ADDRESS_KEY)}
            >
              {f({ id: 'tables.tripsOverview.column.address' })}
            </TooltipComponent>
          </div>
        );

      case INFO_KEY:
        return <ThInfoForRide id={INFO_KEY} />;

      case DISTANCE_KEY:
        return <ThDistance id={DISTANCE_KEY} />;

      case DRIVER_KEY:
        return <ThDriver id={DRIVER_KEY} />;

      case DURATION_KEY:
        return <ThDuration id={DURATION_KEY} />;

      case CO2_EMISSION_KEY:
        return <ThCo2Emission id={CO2_EMISSION_KEY} />;

      case ECO_DRIVE_KEY:
        return <ThEcoDrive id={ECO_DRIVE_KEY} />;

      case FUEL_CONSUMPTION_CAN_KEY:
        return <ThConsumption id={FUEL_CONSUMPTION_CAN_KEY} />;

      case AVARAGE_CONSUMPTION_KEY:
        return <ThAverageConsumption id={AVARAGE_CONSUMPTION_KEY} />;

      case INFO_FOR_PARKING_KEY:
        return (
          <div className="th-sticky" style={{ whiteSpace: 'nowrap', minWidth: '130px', flex: 1 }}>
            <TooltipComponent
              id={`ridebook-${INFO_FOR_PARKING_KEY}`}
              overlay={
                <div style={{ color: '#fff', whiteSpace: 'pre-wrap', fontWeight: 'normal' }}>
                  {`${f({
                    id: 'enum.expenseTypes.parking',
                  })}: `}
                  <strong>
                    {`${f({
                      id: 'tables.tripsOverview.header.tooltip.parking',
                    })}`}
                  </strong>
                </div>
              }
              width="300px"
              style={{ top: '35px' }}
              positionLeft={getIsColumnLast(columnsTable, INFO_FOR_PARKING_KEY)}
            >
              {f({ id: 'enum.expenseTypes.parking' })}
            </TooltipComponent>
          </div>
        );

      case CUSTOMER_KEY:
        return <ThCustomer id={CUSTOMER_KEY} />;

      case MAXIMAL_SPEED_KEY:
        return <ThMaximalSpeed id={MAXIMAL_SPEED_KEY} />;

      case AVARAGE_SPEED_KEY:
        return <ThAvarageSpeed id={AVARAGE_SPEED_KEY} />;

      case MILEAGE_END_KEY:
        return <ThMileageEnd id={MILEAGE_END_KEY} />;

      case INCREASE_KEY:
        return <ThIncrease id={INCREASE_KEY} />;

      case DECLINE_KEY:
        return <ThDecline id={DECLINE_KEY} />;

      case SOURCE_KEY:
        return <ThSource id={SOURCE_KEY} />;

      case ACTIONS_KEY:
        return (
          <div
            className="w-sm text-center th-sticky sticky-col actions justify-content-center"
            style={{ whiteSpace: 'nowrap', minWidth: '60px', maxWidth: '60px', width: '60px' }}
          >
            {/* {f({ id: 'table.actionsBtn' })} */}
          </div>
        );

      default:
        return null;
    }
  };

  const isRideBlacklistEmpty = React.useCallback(() => {
    let isEmpty = true;
    Object.keys(checkedTableItems.rideIdBlacklist).forEach((customerId: string) => {
      if (checkedTableItems.rideIdBlacklist[customerId].length > 0) {
        isEmpty = false;
      }
    });
    return isEmpty;
  }, [checkedTableItems.rideIdBlacklist]);

  const isRideListEmpty = React.useCallback(() => {
    let isEmpty = true;
    Object.keys(checkedTableItems.rideIds).forEach((customerId: string) => {
      if (checkedTableItems.rideIds[customerId].length > 0) {
        isEmpty = false;
      }
    });
    return isEmpty;
  }, [checkedTableItems.rideIds]);

  const getCheckedItemsCount = React.useCallback(() => {
    let count = 0;
    Object.keys(checkedTableItems.rideIds).forEach((customerId: string) => {
      count += checkedTableItems.rideIds[customerId].length;
    });
    if (tableData) {
      checkedTableItems.intervals.forEach((interval: { min: string; max: string }) => {
        tableData.summaries
          .filter((i) => i.type !== OVERVIEW_KEY)
          .forEach((item) => {
            if (item.from === interval.min && item.to === interval.max) {
              count += item.rideCount || 0;
            }
          });
      });
    }
    return count;
  }, [checkedTableItems.intervals, checkedTableItems.rideIds, tableData]);

  React.useEffect(() => {
    const checkbox = document.getElementById('checkallrecords') as HTMLInputElement;

    if (checkbox) {
      const count: number = tableData?.availableCount || 0;
      const isTableChecked = checkedTableItems.mainCheckbox;
      if (
        count > 0 &&
        (isTableChecked || getCheckedItemsCount() === count) &&
        isRideBlacklistEmpty() &&
        checkedTableItems.intervalsBlacklist.length === 0
      ) {
        checkbox.checked = true;
        checkbox.indeterminate = false;
      } else if (
        count > 0 &&
        (isTableChecked || !isRideListEmpty() || checkedTableItems.intervals.length > 0)
      ) {
        checkbox.indeterminate = true;
        checkbox.checked = false;
      } else {
        checkbox.indeterminate = false;
        checkbox.checked = false;
      }
    }
  }, [checkedTableItems, getCheckedItemsCount, isRideBlacklistEmpty, isRideListEmpty, tableData]);

  const toFixTableHeader = () => {
    const rideDetailHeight =
      document.getElementsByClassName('w-selection-summary')[0].clientHeight &&
      window.matchMedia('(min-width: 992px)').matches
        ? document.getElementsByClassName('w-selection-summary')[0].clientHeight
        : 0;
    const pageWrapper = document.getElementById('page-content-wrapper-div');
    const tableWrapper = document.getElementById('table-overview');
    if (pageWrapper && tableWrapper) {
      const scroll = pageWrapper.scrollTop;
      let i = 0;
      const thElements: any = document.querySelectorAll('#table-overview .th-sticky');
      if (scroll >= tableWrapper.offsetTop - rideDetailHeight) {
        for (i = 0; i < thElements.length; i += 1) {
          thElements[i].style.top = `${scroll - tableWrapper.offsetTop - 2 + rideDetailHeight}px`;
        }
        return false;
      }
      for (i = 0; i < thElements.length; i += 1) {
        thElements[i].style.top = 0;
      }
      return false;
    }
    return false;
  };

  React.useEffect(() => {
    const pageWrapper = document.getElementById('page-content-wrapper-div');
    const tableHeader = document.getElementById('table-overview-header');
    const tableHeaderActions = document.getElementById('table-overview-header-actions');
    const tableWrapper = document.getElementById('table-overview');
    let pageWrapperIsScrolling: NodeJS.Timeout;

    if (pageWrapper && tableHeader && tableHeaderActions && tableWrapper) {
      pageWrapper.addEventListener(
        'scroll',
        () => {
          const scroll = pageWrapper.scrollTop;
          if (scroll > tableWrapper.offsetTop) {
            tableHeader.classList.add('b-table-prehlad-jazd-header--scroll');
            tableHeaderActions.classList.add('w-selection-summary-actions--scroll');
          }

          window.clearTimeout(pageWrapperIsScrolling);
          pageWrapperIsScrolling = setTimeout(() => {
            tableHeader.classList.remove('b-table-prehlad-jazd-header--scroll');
            tableHeaderActions.classList.remove('w-selection-summary-actions--scroll');
            toFixTableHeader();
          }, 300);
        },
        { passive: true }
      );
    }
    return () => {
      if (pageWrapper) {
        pageWrapper.removeEventListener('scroll', toFixTableHeader);
      }
    };
  }, []);

  return (
    <div ref={refSize} id="table-overview-header" className="b-table-prehlad-jazd-header">
      <div className="b-table-prehlad-jazd-header-row">
        <div
          className="w-sm th-sticky text-center justify-content-center b-table-prehlad-jazd-checkbox"
          style={{
            minWidth: '40px',
            maxWidth: '40px',
            width: '40px',
          }}
        >
          <div
            className="e-checkbox checkbox-sm"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <label>
              <input
                id="checkallrecords"
                type="checkbox"
                onChange={props.selectAllRows}
                data-customer-id={rideItem?.customer.id}
                className="js-checkbox-indeterminate"
              />
              <span className="p-0" />
            </label>
          </div>
        </div>

        {props.columns &&
          props.columns.map((column) => (
            <React.Fragment key={column.key}>{renderHeaderCell(column)}</React.Fragment>
          ))}
      </div>
    </div>
  );
}

export default TableHeader;
